import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { rateFight, removeFightRating } from "../../http/auth";
import { useAuthStore } from "../../store/authStore";

const RATING_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/**
 * Community "how great was this fight" rating, 1-10.
 *
 * Sits inside the fight card beneath the vote result bar. Any logged-in user
 * can rate without having voted, and can change their rating at any time.
 * Clicking the currently selected chip clears the rating.
 *
 * @param {object} props
 * @param {string} props.fightID
 * @param {number|null} props.average Community average, or null when unrated
 * @param {number} props.count Number of ratings behind the average
 * @param {number|null} props.userRating The current user's rating, if any
 */
export const GreatFightRating = ({ fightID, average, count, userRating }) => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const ratingMutation = useMutation({
    mutationFn: (payload) =>
      payload.remove ? removeFightRating(payload) : rateFight(payload),
    // Returned so the mutation stays pending until the refetch lands. Without
    // it the chips re-enable while still showing the old selection, letting a
    // fast second click act on state the server has already changed.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["fight", fightID] }),
    onError: () => toast.error("Failed to update rating", { autoClose: 5000 }),
  });

  const handleRate = (rating) => {
    if (!user) return toast.error("You must be logged in to rate", { autoClose: 5000 });
    if (rating === userRating) {
      return ratingMutation.mutate({ id: fightID, remove: true });
    }
    ratingMutation.mutate({ id: fightID, rating });
  };

  const hasRatings = count > 0 && average !== null && average !== undefined;

  return (
    <div className="mt-6 pt-6 border-t border-flyers-dark-gray">
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
        {/* Average */}
        <div className="flex items-baseline gap-2 sm:flex-col sm:items-center sm:gap-0 sm:min-w-[5rem]">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wide text-flyers-mid-gray sm:hidden">
            Great Fight?
          </h3>
          {hasRatings ? (
            <>
              <span className="font-heading text-3xl sm:text-4xl font-black text-flyers-orange leading-none">
                {Number(average).toFixed(1)}
              </span>
              <span className="font-body text-xs text-flyers-mid-gray sm:mt-1">
                /10 · {count} rating{count !== 1 ? "s" : ""}
              </span>
            </>
          ) : (
            <>
              <span className="font-heading text-3xl sm:text-4xl font-black text-flyers-dark-gray leading-none">
                —
              </span>
              <span className="font-body text-xs text-flyers-mid-gray sm:mt-1">
                Be the first to rate
              </span>
            </>
          )}
        </div>

        {/* Chips */}
        <div className="flex flex-col items-center">
          <h3 className="hidden sm:block font-heading text-sm font-bold uppercase tracking-wide text-flyers-mid-gray mb-2">
            Great Fight?
          </h3>
          <div className="grid grid-cols-5 sm:flex sm:flex-wrap justify-center gap-1.5">
            {RATING_VALUES.map((value) => {
              const isSelected = value === userRating;
              return (
                <button
                  key={value}
                  onClick={() => handleRate(value)}
                  disabled={ratingMutation.isPending}
                  aria-label={
                    isSelected
                      ? `Remove your rating of ${value}`
                      : `Rate this fight ${value} out of 10`
                  }
                  aria-pressed={isSelected}
                  title={isSelected ? "Click to remove your rating" : undefined}
                  className={`h-9 sm:w-9 rounded font-heading font-bold text-sm transition-all duration-150 ${
                    isSelected
                      ? "bg-flyers-orange text-white shadow-orange-glow"
                      : "border border-flyers-dark-gray text-flyers-light-gray bg-flyers-charcoal hover:border-flyers-orange hover:text-white"
                  } ${ratingMutation.isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {value}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
